import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { CaseEventDTO } from 'api/model';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { useQueryClient } from '@tanstack/react-query';
import { useCaseMarkEventsAsReadByAdmin } from 'api/case/case';
import {
  getTransportLogisticsGetAssignmentBookedToEditedByFieldtesterQueryKey,
  useTransportLogisticsGetAssignmentBookedToEditedByFieldtester,
} from 'api/transport-logistics/transport-logistics';
import { formatDateTime } from 'utils/date-helpers';
import CaseOpenLink from 'components/CaseOpenLink';
import Checkbox from 'components/inputs/Checkbox';
import useUsers from 'contexts/basicData/useUsers';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import useBaseCaseEventWidgetColumnSettings from './useBaseCaseEventWidgetColumnSettings';
import {
  Centered,
  getTableRowClassName,
  SavingRowAnimationWrapper,
} from 'components/Dashboard/table-helpers';

const TABLE_NAME = 'booked-to-edited-by-ft';

const BookedToEditedByFTWidget = () => {
  const [editedBookedTos, setEditedBookedTos] = useState<CaseEventDTO[]>([]);
  const [ongoingSaves, setOngoingSaves] = useState<number[]>([]);
  const users = useUsers();

  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();

  const queryClient = useQueryClient();
  const { mutateAsync: markEventAsRead, status: markAsReadStatus } =
    useCaseMarkEventsAsReadByAdmin({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries([
            `${getTransportLogisticsGetAssignmentBookedToEditedByFieldtesterQueryKey()}`,
          ]);
        },
      },
    });

  const getBookedToEdits =
    useTransportLogisticsGetAssignmentBookedToEditedByFieldtester(
      {
        startDate: daterange[0] ?? undefined,
        endDate: daterange[1] ?? undefined,
        businessUnitIDs: selectedBusinessUnits,
      },
      {
        query: {
          refetchInterval: UPDATE_INTERVAL,
        },
      }
    );

  useEffect(() => {
    if (getBookedToEdits.data) {
      setEditedBookedTos(getBookedToEdits.data);
    } else {
      setEditedBookedTos([] as CaseEventDTO[]);
    }
  }, [getBookedToEdits.data]);

  const filteredEditedBookedTos = useMemo(() => {
    if (!searchText.length) {
      return editedBookedTos;
    }
    return editedBookedTos
      ? editedBookedTos.filter((edits) => {
          return (
            edits.registrationNumber
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (users[edits.createdByID]?.name ?? '')
              .toLowerCase()
              .includes(searchText.toLowerCase())
          );
        })
      : [];
  }, [editedBookedTos, searchText, users]);

  const baseColumnSettings = useBaseCaseEventWidgetColumnSettings();

  const columnSettings: ColumnSetting<CaseEventDTO, any>[] = useMemo(() => {
    const columns: ColumnSetting<CaseEventDTO, any>[] =
      baseColumnSettings.concat([
        {
          head: 'Justerat vid',
          render: (row) => `${formatDateTime(row.created)}`,
          width: 100,
          sortFunction: (a, b) => a.created.getTime() - b.created.getTime(),
        },
        {
          head: ' ',
          render: (row) => (
            <Centered>
              <CaseOpenLink caseID={row.caseID} />
            </Centered>
          ),
          width: 20,
        },
        {
          head: 'Klart',
          render: (row) => (
            <Centered>
              <Checkbox
                disabled={markAsReadStatus === 'loading'}
                onChange={async () => {
                  setOngoingSaves([...ongoingSaves, row.id]);
                  await markEventAsRead({ data: [row.id] });
                  setOngoingSaves(ongoingSaves.filter((id) => id !== row.id));
                }}
              />
            </Centered>
          ),
          width: 30,
        },
      ]);
    return columns;
  }, [baseColumnSettings, markAsReadStatus, markEventAsRead, ongoingSaves]);

  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.BookedToEditedByFT]}
      count={editedBookedTos.length}
      isLoading={getBookedToEdits.isLoading}
      info="Markera som klar för att markera som läst."
    >
      <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      >
        <Table
          columnSettings={columnSettings}
          rows={filteredEditedBookedTos}
          rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
          useColumnWidthAsFlexAmount
        />
      </SavingRowAnimationWrapper>
    </Widget>
  );
};

export default BookedToEditedByFTWidget;
